import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomFormatItem,
  JobType,
} from "../../../docuclipper/DocuclipperTypes";
import { ReduxState } from "../../../redux";
import {
  addCustomFormatItemRedux,
  createCustomFormatRedux,
  deleteSelectedCustomFormat,
  fetchCustomFormats,
  reorderCustomFormatItemsRedux,
  setSelectedCustomFormat,
  updateCustomFormatName,
} from "../../../redux/reducers/CustomFormat";
import { setSelectedExportType } from "../../../redux/reducers/DownloadOptions";
import PageHeader from "../../Docuclipper/PageHeader/PageHeader";
import CustomFormatItemComp from "./CustomFormatItem";

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  ...draggableStyle,
  userSelect: "none" as const,
  background: isDragging ? "#e3f2fd" : "white",
  border: isDragging ? "2px solid #2196f3" : "1px solid #ddd",
  borderRadius: "4px",
  padding: "12px",
  marginRight: "8px",
  minWidth: "220px",
  maxWidth: "260px",
  flex: "0 0 auto",
});

const getListStyle = (isDraggingOver: boolean) => ({
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "stretch",
  overflowX: "auto" as const,
  background: isDraggingOver ? "#f0f8ff" : "#fafafa",
  padding: "8px",
  borderRadius: "4px",
  border: isDraggingOver ? "2px dashed #2196f3" : "1px solid #ddd",
});

export const getCustomFormatHeading = (cfi: CustomFormatItem) => {
  if (cfi.heading?.text) {
    return cfi.heading.text;
  }
  if (!cfi.source) {
    return "";
  }
  if (cfi.source === "checkNumber") {
    return "Check Number";
  } else if (cfi.source === "refNumber") {
    return "Reference Number";
  } else if (cfi.source === "categoryKeyword") {
    return "Vendor/Keyword";
  } else if (cfi.source === "name") {
    return "Description";
  }
  return cfi.source.slice(0, 1).toUpperCase() + cfi.source.slice(1);
};

export const getValue = (cfi: CustomFormatItem, jobType: JobType) => {
  if (!cfi.source) {
    return "";
  }

  if (jobType === "Invoice") {
    switch (cfi.source) {
      case "id":
        return "123";
      case "date":
        return "2022-01-10";
      case "item":
        return "Item 1";
      case "tax":
      case "total":
      case "quantity":
      case "unitPrice":
      case "price":
        return 123;
      case "service":
        return "Service 1";
      case "category":
        return "Category 1";
      case "customer":
        return "Customer 1";
      case "vendor":
        return "Vendor 1";
      default:
        return "";
    }
  }

  switch (cfi.source) {
    case "date":
    case "date2":
      return "2022-01-10";
    case "amount":
    case "debit":
    case "credit":
    case "balance":
      return 123.45;
    case "name":
    case "description":
      return "Transaction 1";
    case "checkNumber":
      return "1000";
    case "refNumber":
      return "1000";
    case "account":
      return "Account 1";
    case "autoIncrement":
      return "1"; // This will be incremented for each row in the actual implementation
    case "transactionType":
      return `${cfi.creditLabel || "Credit"}`;
    case "hardcodedValue":
      return cfi.hardcodedValue || "Sample Value";
    default:
      return "";
  }
};

const ConfigNameEditor: React.FC<{
  name: string;
  onRename: (value: string) => void;
}> = ({ name, onRename }) => {
  const [value, setValue] = React.useState(name);
  // Keep the input in sync when a different configuration is selected.
  React.useEffect(() => setValue(name), [name]);

  return (
    <Form.Group className="mb-0">
      <Form.Label className="small text-muted mb-1">
        Configuration name
      </Form.Label>
      <div className="d-flex align-items-center">
        <Form.Control
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            const trimmed = value.trim();
            if (trimmed && trimmed !== name) {
              onRename(trimmed);
            } else {
              setValue(name);
            }
          }}
          title="Rename this configuration"
          style={{
            fontWeight: 600,
            fontSize: "1.25rem",
            border: "none",
            borderBottom: "1px dashed #adb5bd",
            borderRadius: 0,
            padding: "2px 0",
            boxShadow: "none",
            backgroundColor: "transparent",
            maxWidth: "320px",
          }}
        />
        <FontAwesomeIcon icon="pencil-alt" className="text-muted ml-2" />
      </div>
    </Form.Group>
  );
};

type Props = {
  jobType: JobType;
};

export const CustomFormat: React.FC<Props> = ({
  jobType,
}: {
  jobType: JobType;
}) => {
  const cfType = jobType === "Invoice" ? "invoice" : "bankStatement";
  const { formatsByType, selected } = useSelector(
    (state: ReduxState) => state.CustomFormat
  );
  const customFormats = formatsByType[cfType];
  React.useEffect(() => {
    dispatch(
      fetchCustomFormats({
        init: true,
        type: cfType,
      })
    );
  }, [jobType]);

  const dispatch = useDispatch();

  const customFormatsFiltered = customFormats.filter(
    (cf) => cf.name !== "basic" && cf.name !== "all"
  );

  return (
    <PageHeader title="" description="">
      <div>
        <Row className="justify-content-end text-right">
          <Button
            className="mx-2"
            variant="link"
            onClick={() =>
              dispatch(
                createCustomFormatRedux({
                  type: jobType === "Invoice" ? "invoice" : "bankStatement",
                })
              )
            }
          >
            <FontAwesomeIcon icon="plus" />
            <span className="ml-2">Add new configuration</span>
          </Button>
        </Row>

        <Row className="align-items-center mx-2">
          {customFormatsFiltered.length > 0 && (
            <Col md="4" className="mt-2">
              <Form.Group className="mt-3">
                <Form.Label className="small text-muted mb-1">
                  Configuration
                </Form.Label>
                <Form.Control
                  value={selected?.name}
                  as="select"
                  onChange={(e) => {
                    dispatch(setSelectedCustomFormat(e.target.value));
                    dispatch(
                      setSelectedExportType(`custom-${e.target.value}` as any)
                    );
                  }}
                >
                  {customFormatsFiltered.map((cf, i) => (
                    <option key={i} value={cf.name}>
                      {cf.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          )}

          {customFormatsFiltered.length === 0 && (
            <Alert variant="info">
              You don't have any configurations. Click "Add new configuration"
              to create one.
            </Alert>
          )}
          {selected && (
            <Col>
              <div className="d-flex align-items-end flex-wrap">
                <ConfigNameEditor
                  name={selected.name}
                  onRename={(newValue) =>
                    dispatch(updateCustomFormatName(newValue))
                  }
                />
                <Button
                  className="ml-3 mb-1"
                  variant="link"
                  onClick={() => dispatch(deleteSelectedCustomFormat())}
                >
                  <FontAwesomeIcon icon="trash" />
                  <span className="ml-2">Delete Configuration</span>
                </Button>
              </div>
            </Col>
          )}
        </Row>

        {selected && (
          <>
            <Row className="align-items-center justify-content-between mx-2 mt-3">
              <Col>
                <div className="h4 mb-0">Columns</div>
                <small className="text-muted">
                  Drag the <FontAwesomeIcon icon="arrows-alt" /> handle to
                  reorder. Columns appear left-to-right in your export, exactly
                  as shown here.
                </small>
              </Col>
              <Col xs="auto">
                <Button
                  variant="link"
                  onClick={() => {
                    dispatch(addCustomFormatItemRedux());
                  }}
                >
                  <FontAwesomeIcon icon="plus" />
                  <span className="ml-2">Add Column</span>
                </Button>
              </Col>
            </Row>
            <div className="m-4">
              <DragDropContext
                onDragEnd={(result: DropResult) => {
                  if (!result.destination) {
                    return;
                  }
                  dispatch(
                    reorderCustomFormatItemsRedux({
                      startIndex: result.source.index,
                      endIndex: result.destination.index,
                    })
                  );
                }}
              >
                <Droppable
                  droppableId="custom-format-columns"
                  direction="horizontal"
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={getListStyle(snapshot.isDraggingOver)}
                    >
                      {(selected.data.columnConfigs || []).map((cfi, idx) => (
                        <Draggable
                          key={cfi.uid || `column-${cfi.index}`}
                          draggableId={cfi.uid || `column-${cfi.index}`}
                          index={idx}
                        >
                          {(dProvided, dSnapshot) => (
                            <div
                              ref={dProvided.innerRef}
                              {...dProvided.draggableProps}
                              style={getItemStyle(
                                dSnapshot.isDragging,
                                dProvided.draggableProps.style
                              )}
                            >
                              <CustomFormatItemComp
                                cfi={cfi}
                                jobType={jobType}
                                previewValue={getValue(cfi, jobType)}
                                dragHandleProps={dProvided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </>
        )}
      </div>
    </PageHeader>
  );
};
